import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { toast } from 'sonner';

import { EditCheckboxField } from '../../../EditCheckboxField';
import { EditCompanyNameField } from '../../../EditCompanyNameField';
import { EditDialog } from '../../../EditDialog';
import { EditDynamicSelectField } from '../../../EditDynamicSelectField';
import { EditFormField } from '../../../EditFormField';
import type { ContactPersonalInfo } from '../queries/contactSummaryQuery';
import { GET_CONTACT_SUMMARY } from '../queries/contactSummaryQuery';

interface FieldMapping {
  apiField: string;
  formField: keyof ContactPersonalInfo;
  isDate?: boolean;
}

const PERSONAL_INFO_FIELDS: FieldMapping[] = [
  { apiField: 'salutation', formField: 'salutation' },
  { apiField: 'firstName', formField: 'firstName' },
  { apiField: 'middleName', formField: 'middleName' },
  { apiField: 'lastName', formField: 'lastName' },
  { apiField: 'suffix', formField: 'suffix' },
  { apiField: 'nickname', formField: 'nickname' },
  { apiField: 'birthday', formField: 'birthday', isDate: true },
  { apiField: 'anniversary', formField: 'anniversary', isDate: true },
  { apiField: 'gender', formField: 'gender' },
  { apiField: 'maritalStatus', formField: 'maritalStatus' },
  { apiField: 'spousePartnerName', formField: 'spousePartnerName' },
  { apiField: 'isMinor', formField: 'isMinor' },
  {
    apiField: 'isMinorWithParentalConsent',
    formField: 'isMinorWithParentalConsent'
  },
  { apiField: 'companyName', formField: 'companyNameId' }
];

function getChangedValues(
  fields: FieldMapping[],
  current: ContactPersonalInfo,
  initial: ContactPersonalInfo
): Record<string, boolean | null | string> {
  const changes: Record<string, boolean | null | string> = {};

  for (const { apiField, formField, isDate } of fields) {
    const currentVal = current[formField];
    const initialVal = initial[formField];

    const hasChanged = isDate
      ? toDateInputValue(currentVal as string) !==
        toDateInputValue(initialVal as string)
      : currentVal !== initialVal;

    if (hasChanged) {
      if (isDate) {
        changes[apiField] = currentVal === '' ? null : (currentVal as string);
      } else if (typeof currentVal === 'boolean') {
        changes[apiField] = currentVal;
      } else if (apiField === 'companyName') {
        changes[apiField] = currentVal === '' ? null : currentVal;
      } else {
        changes[apiField] = currentVal === '' ? null : currentVal;
      }
    }
  }

  return changes;
}

function toDateInputValue(isoDate: string): string {
  if (!isoDate) return '';
  const date = new Date(isoDate);
  return isNaN(date.getTime()) ? isoDate : date.toISOString().split('T')[0];
}

const UPDATE_CONTACT = gql`
  mutation UpdateContact($id: ID!, $values: ContactInput!) {
    updateContact(id: $id, values: $values) {
      ok
      contact {
        id
        salutation
        firstName
        middleName
        lastName
        suffix
        nickname
        birthday
        anniversary
        gender
        maritalStatus
        spousePartnerName
        isMinor
        isMinorWithParentalConsent
        companyName {
          __typename
          ... on AccountNode {
            id
            name
          }
          ... on ContactNode {
            id
            name
          }
        }
      }
    }
  }
`;

export interface EditPersonalInfoDialogProps {
  contactId: string;
  initialValue: ContactPersonalInfo;
  isOpen: boolean;
  onChange: (data: ContactPersonalInfo) => void;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  value: ContactPersonalInfo;
}

export function EditPersonalInfoDialog({
  contactId,
  initialValue,
  isOpen,
  onChange,
  onOpenChange,
  onSave,
  value
}: EditPersonalInfoDialogProps) {
  const handleCompanyNameChange = (id: null | string, name: string) => {
    onChange({
      ...value,
      companyName: name,
      companyNameId: id ?? ''
    });
  };
  const [isSaving, setIsSaving] = useState(false);

  const [updateContact] = useMutation(UPDATE_CONTACT, {
    refetchQueries: [
      { query: GET_CONTACT_SUMMARY, variables: { id: contactId } }
    ]
  });

  const changes = getChangedValues(PERSONAL_INFO_FIELDS, value, initialValue);
  const isDirty = Object.keys(changes).length > 0;

  const updateField = <K extends keyof ContactPersonalInfo>(
    field: K,
    fieldValue: ContactPersonalInfo[K]
  ) => {
    onChange({ ...value, [field]: fieldValue });
  };

  const handleSave = async () => {
    if (!isDirty) {
      onSave();
      return;
    }

    setIsSaving(true);

    try {
      const response = await updateContact({
        variables: { id: contactId, values: changes }
      });

      const data = response.data as {
        updateContact?: { ok?: boolean };
      };

      if (data.updateContact?.ok === false) {
        toast.error('Failed to save personal information');
      } else {
        toast.success('Personal information saved successfully');
        onSave();
      }
    } catch (error) {
      console.error('Error saving personal information:', error);
      toast.error('Failed to save personal information');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <EditDialog
      isDirty={isDirty}
      isOpen={isOpen}
      isSaving={isSaving}
      onOpenChange={onOpenChange}
      onSave={handleSave}
      title="Edit Personal Information"
    >
      <div className="grid grid-cols-2 gap-6">
        <EditFormField
          label="Salutation"
          onChange={salutation => {
            updateField('salutation', salutation);
          }}
          value={value.salutation}
        />
        <EditFormField
          label="First Name"
          onChange={firstName => {
            updateField('firstName', firstName);
          }}
          value={value.firstName}
        />
        <EditFormField
          label="Middle Name"
          onChange={middleName => {
            updateField('middleName', middleName);
          }}
          value={value.middleName}
        />
        <EditFormField
          label="Last Name"
          onChange={lastName => {
            updateField('lastName', lastName);
          }}
          value={value.lastName}
        />
        <EditFormField
          label="Suffix"
          onChange={suffix => {
            updateField('suffix', suffix);
          }}
          value={value.suffix}
        />
        <EditFormField
          label="Nickname"
          onChange={nickname => {
            updateField('nickname', nickname);
          }}
          value={value.nickname}
        />
        <EditFormField
          label="Birthday"
          onChange={birthday => {
            updateField('birthday', birthday);
          }}
          type="date"
          value={toDateInputValue(value.birthday)}
        />
        <EditFormField
          label="Anniversary"
          onChange={anniversary => {
            updateField('anniversary', anniversary);
          }}
          type="date"
          value={toDateInputValue(value.anniversary)}
        />
        <EditDynamicSelectField
          fieldName="gender"
          label="Gender"
          model="res.partner"
          onChange={v => {
            updateField('gender', v);
          }}
          value={value.gender}
        />
        <EditDynamicSelectField
          fieldName="marital_status"
          label="Marital Status"
          model="res.partner"
          onChange={v => {
            updateField('maritalStatus', v);
          }}
          value={value.maritalStatus}
        />
        <EditFormField
          label="Spouse/Partner Name"
          onChange={spousePartnerName => {
            updateField('spousePartnerName', spousePartnerName);
          }}
          value={value.spousePartnerName}
        />
        <EditCheckboxField
          checked={value.isMinor}
          label="Is Minor"
          onCheckedChange={isMinor => {
            updateField('isMinor', isMinor);
          }}
        />
        <EditCheckboxField
          checked={value.isMinorWithParentalConsent}
          label="Is Minor with Parental Consent"
          onCheckedChange={isMinorWithParentalConsent => {
            updateField(
              'isMinorWithParentalConsent',
              isMinorWithParentalConsent
            );
          }}
        />
        <EditCompanyNameField
          excludeContactId={contactId}
          label="Company Name"
          onChange={handleCompanyNameChange}
          value={
            value.companyNameId
              ? { id: value.companyNameId, name: value.companyName }
              : null
          }
        />
      </div>
    </EditDialog>
  );
}
